import { useDealership } from '@/contexts/DealershipContext'
import { Conversation, getConversation, getConversations } from '@/services/conversations.api'
import { getTransferredStopDisplay } from '@/utils/conversation-stop-labels'
import { judgeEvaluationApi } from '@/services/judgeEvaluation.api'
import { Dealership } from '@/types'
import { downloadTranscript } from '@/utils/transcriptExport'
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  LoadingOverlay,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconCheck,
  IconClock,
  IconCreditCard,
  IconDownload,
  IconFileText,
  IconMessage,
  IconPlayerPause,
  IconPlayerStop,
  IconRobot,
  IconScale,
  IconSearch,
  IconSend,
  IconX,
} from '@tabler/icons-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ConversationDetail } from './ConversationDetail'
import { CustomerExternalEventsDock } from './CustomerExternalEventsDock'
import { EvaluationResultsModal } from './EvaluationResultsModal'
import { VehicleInterestBadge } from './VehicleInterestBadge'

type ConversationDeepLinkState = {
  conversationId: string
  customerId: string
  customerName?: string
  customerPhone?: string
  customerEmail?: string
}

function pinDeepLinkedConversation(list: Conversation[], deepLinked: Conversation | null): Conversation[] {
  if (!deepLinked) return list
  const withoutDuplicate = list.filter(c => c.id !== deepLinked.id)
  return [deepLinked, ...withoutDuplicate]
}

export function ConversationsList() {
  const computedColorScheme = useComputedColorScheme('light')
  const { selectedEntity } = useDealership()
  const location = useLocation()
  const navigationState = location.state as ConversationDeepLinkState | null
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [openSummary, setOpenSummary] = useState(false) // State to control summary modal
  const [openEvaluation, setOpenEvaluation] = useState(false) // State to control evaluation modal
  const [evaluationData, setEvaluationData] = useState<any>(null)
  const [loadingEvaluation, setLoadingEvaluation] = useState(false)
  const [evalCustomerIds, setEvalCustomerIds] = useState<Set<string>>(new Set()) // Track which customers have evaluations
  const [totalConversationsCount, setTotalConversationsCount] = useState(0) // Total count from backend
  const [totalActiveConversationsCount, setTotalActiveConversationsCount] = useState(0) // Total active count from backend

  // Infinite scroll state
  const [loadedCount, setLoadedCount] = useState(20)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [allConversations, setAllConversations] = useState<Conversation[]>([]) // Store all loaded conversations
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDeepLinkRef = useRef<ConversationDeepLinkState | null>(null)
  const resolvedDeepLinkConversationRef = useRef<Conversation | null>(null)
  const skipInitialSearchLoadRef = useRef(true)

  useEffect(() => {
    if (navigationState?.conversationId && navigationState.customerId) {
      pendingDeepLinkRef.current = navigationState
      resolvedDeepLinkConversationRef.current = null
    }
  }, [navigationState])

  const scrollSelectedConversationIntoView = useCallback((conversationId: string) => {
    requestAnimationFrame(() => {
      const viewport = scrollAreaRef.current?.querySelector('[data-scrollbar-viewport]') as HTMLElement | null
      const item = viewport?.querySelector(`[data-conversation-id="${conversationId}"]`) as HTMLElement | null
      item?.scrollIntoView({ block: 'nearest' })
    })
  }, [])

  const resolvePendingDeepLink = useCallback(async (dealershipId: string, target: ConversationDeepLinkState) => {
    try {
      const conv = await getConversation(dealershipId, target.conversationId, {
        customerId: target.customerId,
        customerName: target.customerName,
        customerPhone: target.customerPhone,
        customerEmail: target.customerEmail,
      })

      resolvedDeepLinkConversationRef.current = conv
      setSelectedConversation(conv)
      setConversations(prev => pinDeepLinkedConversation(prev, conv))
      setAllConversations(prev => pinDeepLinkedConversation(prev, conv))
      scrollSelectedConversationIntoView(conv.id)
    } catch (error) {
      console.error('Failed to resolve conversation deep link:', error)
      setSelectedConversation(null)
      notifications.show({
        title: 'Conversation not found',
        message: 'Could not load the conversation for this appointment.',
        color: 'red',
      })
    } finally {
      pendingDeepLinkRef.current = null
    }
  }, [scrollSelectedConversationIntoView])

  const loadConversations = useCallback(
    async (append = false) => {
      if (!selectedEntity || selectedEntity.type !== 'dealership') return

      try {
        if (append) {
          setIsLoadingMore(true)
        } else {
          setIsLoading(true)
        }

        const dealership = selectedEntity as Dealership
        const targetLimit = append ? loadedCount + 20 : 20

        const response = await getConversations(dealership.id, {
          limit: targetLimit,
          searchQuery: searchQuery || undefined,
          includeAppointments: true,
          status: statusFilter === 'all' ? undefined : statusFilter === 'stopped' ? 'TRANSFERRED' : statusFilter.toUpperCase(),
        })

        const allFetchedConversations = response.conversations || []
        const totalFromBackend = response.total || 0
        const totalActiveFromBackend = response.totalActive || 0

        // Update total counts from backend
        if (!append) {
          setTotalConversationsCount(totalFromBackend)
          // All conversations are AI-handled, so active = currently active conversations
          setTotalActiveConversationsCount(totalActiveFromBackend)
        }

        if (append) {
          // When appending, we fetch more but only show the new ones
          setAllConversations(prev => {
            const existingIds = new Set(prev.map(c => c.id))
            const newConversations = allFetchedConversations.filter(c => !existingIds.has(c.id))
            return pinDeepLinkedConversation([...prev, ...newConversations], resolvedDeepLinkConversationRef.current)
          })
          setLoadedCount(targetLimit)
          setHasMore(allFetchedConversations.length >= targetLimit)
          setConversations(
            pinDeepLinkedConversation(allFetchedConversations.slice(0, targetLimit), resolvedDeepLinkConversationRef.current),
          )
        } else {
          // Initial load or search reset
          setAllConversations(
            pinDeepLinkedConversation(allFetchedConversations, resolvedDeepLinkConversationRef.current),
          )
          setLoadedCount(20)
          setHasMore(allFetchedConversations.length >= 20)
          setConversations(
            pinDeepLinkedConversation(allFetchedConversations.slice(0, 20), resolvedDeepLinkConversationRef.current),
          )
        }

        // Access allConversations to satisfy linter (state is used via functional updates)
        void allConversations

        // Auto-select on initial load; resolve appointment deep-links without flashing another thread
        if (!append) {
          const pendingDeepLink = pendingDeepLinkRef.current

          if (pendingDeepLink) {
            const match = allFetchedConversations.find(c => c.id === pendingDeepLink.conversationId)
            if (match) {
              pendingDeepLinkRef.current = null
              resolvedDeepLinkConversationRef.current = match
              setSelectedConversation(match)
              setConversations(prev => pinDeepLinkedConversation(prev, match))
              scrollSelectedConversationIntoView(match.id)
            } else {
              void resolvePendingDeepLink(dealership.id, pendingDeepLink)
            }
          } else {
            const fallback = allFetchedConversations[0] ?? null
            if (fallback) {
              setSelectedConversation(prev => prev || fallback)
            }
          }
        }
      } catch (error) {
        console.error('Failed to load conversations:', error)
        if (!append) {
          setConversations([])
          setAllConversations([])
          setSelectedConversation(null)
        }
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [selectedEntity, loadedCount, searchQuery, statusFilter, resolvePendingDeepLink, scrollSelectedConversationIntoView],
  )

  useEffect(() => {
    if (selectedEntity && selectedEntity.type === 'dealership') {
      // Reset pagination when entity changes
      setLoadedCount(20)
      setHasMore(true)
      setAllConversations([])
      loadConversations()
    }
  }, [selectedEntity, loadConversations])

  // Debounced search effect (initial load is handled by the entity effect above)
  useEffect(() => {
    if (skipInitialSearchLoadRef.current) {
      skipInitialSearchLoadRef.current = false
      return
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Reset pagination when search changes
    setLoadedCount(20)
    setHasMore(true)
    setAllConversations([])

    // Only search if query is empty (show all) or has at least 3 characters
    const trimmedQuery = searchQuery.trim()
    const shouldSearch = trimmedQuery.length === 0 || trimmedQuery.length >= 3

    if (!shouldSearch) {
      // If less than 3 characters and not empty, don't search yet
      return
    }

    // Debounce search API call (500ms delay to let user finish typing)
    searchTimeoutRef.current = setTimeout(() => {
      if (selectedEntity && selectedEntity.type === 'dealership') {
        loadConversations()
      }
    }, 500)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery, loadConversations, selectedEntity])

  // Check which customers have evaluation results (to identify eval customers)
  useEffect(() => {
    const checkEvalCustomers = async () => {
      if (conversations.length === 0) {
        setEvalCustomerIds(new Set())
        return
      }
      if (!selectedEntity || selectedEntity.type !== 'dealership') {
        setEvalCustomerIds(new Set())
        return
      }
      const dealershipId = (selectedEntity as Dealership).id

      const evalIds = new Set<string>()

      for (const conv of conversations) {
        if (!conv.customerId) continue

        try {
          const evaluation = await judgeEvaluationApi.getEvaluationByCustomer(conv.customerId, dealershipId)
          if (evaluation) {
            evalIds.add(conv.customerId)
          }
        } catch {
          // Not an eval customer, continue silently
        }
      }

      setEvalCustomerIds(evalIds)
    }

    if (conversations.length > 0 && !isLoading) {
      checkEvalCustomers()
    }
  }, [conversations, isLoading, selectedEntity])

  // Silent refresh: updates only the active conversation in-place without
  // loading overlay or full list re-fetch.  Accepts an optional Conversation
  // object (e.g. from polling which already fetched it) to avoid an extra API call.
  const silentRefreshActiveConversation = useCallback(
    async (updatedConv?: Conversation) => {
      if (!selectedConversation) return
      if (!selectedEntity || selectedEntity.type !== 'dealership') return

      try {
        const fresh =
          updatedConv ??
          (await getConversation((selectedEntity as Dealership).id, selectedConversation.id, {
            customerId: selectedConversation.customerId,
            customerName: selectedConversation.customerName,
            customerPhone: selectedConversation.customerPhone,
            customerEmail: selectedConversation.customerEmail,
          }))

        // Update in-place — only replace the matching conversation, keeping
        // all other list items as the same object reference so React skips re-rendering them.
        const replaceMatch = (c: Conversation) => (c.id === fresh.id ? { ...c, ...fresh } : c)
        setConversations(prev => prev.map(replaceMatch))
        setAllConversations(prev => prev.map(replaceMatch))
        setSelectedConversation(prev => (prev?.id === fresh.id ? { ...prev, ...fresh } : prev))
      } catch (error) {
        console.error('Silent refresh failed:', error)
      }
    },
    [selectedEntity, selectedConversation],
  )

  const loadMoreConversations = useCallback(() => {
    if (!isLoadingMore && hasMore && !isLoading) {
      loadConversations(true)
    }
  }, [isLoadingMore, hasMore, isLoading, loadConversations])

  // Handle scroll to load more
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-scrollbar-viewport]') as HTMLElement
    if (!viewport) return

    const handleScroll = () => {
      const scrollTop = viewport.scrollTop
      const scrollHeight = viewport.scrollHeight
      const clientHeight = viewport.clientHeight

      // Load more when user scrolls to 80% of the content
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        loadMoreConversations()
      }
    }

    viewport.addEventListener('scroll', handleScroll)
    return () => {
      viewport.removeEventListener('scroll', handleScroll)
    }
  }, [loadMoreConversations])

  const getConversationStatusBadge = (conversation: Conversation) => {
    if (conversation.status === 'ACTIVE') return null
    if (conversation.status === 'PAUSED') {
      return (
        <Badge size="md" color="yellow" variant="light" leftSection={<IconPlayerPause size={14} />}>
          Paused
        </Badge>
      )
    }
    if (conversation.status === 'TRANSFERRED') {
      const display = getTransferredStopDisplay(conversation)
      return (
        <Tooltip label={display.tooltip}>
          <Badge size="md" color={display.badgeColor} variant="light" leftSection={<IconPlayerStop size={14} />}>
            {display.badgeLabel}
          </Badge>
        </Tooltip>
      )
    }
    return null
  }

  const getStatusBadge = (conversation: Conversation) => {
    const status = conversation.appointmentStatus
    const statusConfig: Record<string, { color: string; label: string; icon: any }> = {
      // Standard appointment statuses
      PENDING: { color: 'yellow', label: 'Pending', icon: IconClock },
      SCHEDULED: { color: 'blue', label: 'Scheduled', icon: IconClock },
      CONFIRMED: { color: 'green', label: 'Confirmed', icon: IconCheck },
      COMPLETED: { color: 'teal', label: 'Completed', icon: IconCheck },
      CANCELLED: { color: 'gray', label: 'Cancelled', icon: IconX },
      NO_SHOW: { color: 'red', label: 'No Show', icon: IconX },
      RESCHEDULED: { color: 'blue', label: 'Rescheduled', icon: IconClock },
      // Recovery statuses
      MISSED_RECOVERY_PENDING: { color: 'orange', label: 'Missed', icon: IconClock },
      MISSED_RECOVERY_IN_PROGRESS: { color: 'orange', label: 'Missed', icon: IconClock },
      SHOWN_UNSOLD: { color: 'violet', label: 'Shown', icon: IconCheck },
      UNSOLD_RECOVERY_PENDING: { color: 'violet', label: 'Shown', icon: IconClock },
      UNSOLD_RECOVERY_IN_PROGRESS: { color: 'violet', label: 'Shown', icon: IconClock },
      ABANDONED: { color: 'gray', label: 'Abandoned', icon: IconX },
      // No appointment
      NO_APPOINTMENT: { color: 'gray', label: 'No Appt', icon: IconX },
    }

    const config = status ? statusConfig[status] : statusConfig.NO_APPOINTMENT
    const Icon = config?.icon || IconX

    return (
      <Badge size="md" color={config?.color || 'gray'} variant="light" leftSection={<Icon size={14} />}>
        {config?.label || 'No Appt'}
      </Badge>
    )
  }

  const getPrequalBadge = (conversation: Conversation) => {
    if (!conversation.prequalStatus) return null

    const statusConfig: Record<string, { color: string; label: string; icon: any }> = {
      PENDING: { color: 'yellow', label: 'Prequal Pending', icon: IconClock },
      SENT: { color: 'blue', label: 'Prequal Sent', icon: IconSend },
      COMPLETED: { color: 'green', label: 'Prequal Done', icon: IconCreditCard },
      DECLINED: { color: 'gray', label: 'Prequal Declined', icon: IconX },
      EXPIRED: { color: 'red', label: 'Prequal Expired', icon: IconClock },
    }

    const config = statusConfig[conversation.prequalStatus]
    if (!config) return null

    const Icon = config.icon

    return (
      <Tooltip label={config.label}>
        <Badge size="sm" color={config.color} variant="light" leftSection={<Icon size={12} />}>
          {config.label}
        </Badge>
      </Tooltip>
    )
  }

  const formatDuration = (startedAt: string) => {
    const now = new Date()
    const start = new Date(startedAt)
    const diffMs = now.getTime() - start.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)

    if (diffHours > 0) {
      return `${diffHours}.${diffMins % 60} hours`
    }
    return `${diffMins} mins`
  }

  // Filter by activeFilter only (search and status are handled by backend)
  const filteredConversations = conversations.filter(conv => {
    const matchesFilter =
      activeFilter === 'all' || (activeFilter === 'active' && conv.aiActive) || (activeFilter === 'agent' && !conv.aiActive)
    return matchesFilter
  })

  // Use backend totals
  // Total: All conversations in the database (not just the 20 loaded)
  // AI: All conversations are AI-handled, so this shows currently active conversations
  const activeCount = totalActiveConversationsCount
  const totalCount = totalConversationsCount

  if (!selectedEntity || selectedEntity.type !== 'dealership') {
    return <Text>Please select a dealership</Text>
  }

  return (
    <Box style={{ height: 'calc(100vh - 150px)', display: 'flex' }}>
      {/* Left Sidebar - Conversations List */}
      <Box style={{ width: '500px', position: 'relative', flexShrink: 0 }}>
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid var(--mantine-color-gray-3)',
          }}
        >
          {/* Header */}
          <Box p="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <IconMessage size={20} />
                <Title order={4}>Live Conversations</Title>
              </Group>
              <Group gap={4}>
                <Badge color="blue" variant="light">
                  {totalCount} total
                </Badge>
                <Badge color="violet" variant="light">
                  {activeCount} Active
                </Badge>
              </Group>
            </Group>

            <TextInput
              placeholder="Search by name, phone, email, vehicle..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              size="sm"
              mb="xs"
            />
            <Select
              size="xs"
              placeholder="Status"
              data={[
                { value: 'all', label: 'All' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'paused', label: 'Paused' },
                { value: 'stopped', label: 'Stopped' },
              ]}
              value={statusFilter}
              onChange={value => value && setStatusFilter(value)}
            />
          </Box>

          {/* Conversations List */}
          <ScrollArea style={{ flex: 1 }} viewportRef={scrollAreaRef}>
            <Stack gap={0}>
              {filteredConversations.map(conversation => {
                const isSelected = selectedConversation?.id === conversation.id
                const selectedBg = computedColorScheme === 'dark' ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-blue-0)'
                return (
                  <Box
                    key={conversation.id}
                    data-conversation-id={conversation.id}
                    p="md"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? selectedBg : 'transparent',
                      boxShadow: isSelected && computedColorScheme === 'dark' ? 'inset 3px 0 0 #ff6f00' : undefined,
                      borderBottom: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
                      transition: 'background-color 0.15s ease',
                      position: 'relative',
                    }}
                    onClick={() => setSelectedConversation(conversation)}
                  >
                    {/* Icons - positioned at bottom right */}
                    <Group gap="xs" style={{ position: 'absolute', bottom: '12px', right: '12px' }}>
                      {/* Evaluation Icon - only show for eval conversations */}
                      {conversation.customerId && evalCustomerIds.has(conversation.customerId) && (
                        <Tooltip label="View Eval">
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="violet"
                            onClick={async e => {
                              e.stopPropagation()
                              if (!selectedEntity || selectedEntity.type !== 'dealership') return
                              const dealership = selectedEntity as Dealership
                              setLoadingEvaluation(true)
                              setOpenEvaluation(true)
                              try {
                                const data = await judgeEvaluationApi.getEvaluationByCustomer(
                                  conversation.customerId,
                                  dealership.id,
                                )
                                setEvaluationData(data)
                              } catch (error) {
                                console.error('Failed to load evaluation:', error)
                                setEvaluationData(null)
                              } finally {
                                setLoadingEvaluation(false)
                              }
                            }}
                          >
                            <IconScale size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Download Transcript">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="dark"
                          onClick={async e => {
                            e.stopPropagation()
                            if (!selectedEntity || selectedEntity.type !== 'dealership') return
                            try {
                              const dealership = selectedEntity as Dealership
                              const conv = await getConversation(dealership.id, conversation.id)
                              downloadTranscript(conv, conv.messages || [])
                            } catch (error) {
                              console.error('Failed to download transcript:', error)
                              notifications.show({
                                title: 'Error',
                                message: 'Failed to download transcript',
                                color: 'red',
                              })
                            }
                          }}
                        >
                          <IconDownload size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {/* Summary Icon - show whenever summary is available */}
                      {conversation.conversationSummary && (
                        <Tooltip label="View Conversation Summary">
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="dark"
                            onClick={e => {
                              e.stopPropagation()
                              setSelectedConversation(conversation)
                              setOpenSummary(true)
                            }}
                          >
                            <IconFileText size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>

                    <Stack gap="xs">
                      {/* Header */}
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                          <Text fw={600} size="sm" truncate style={{ flex: 1 }}>
                            {conversation.customerName}
                          </Text>
                        </Group>
                        <Group gap="xs" align="flex-start">
                          {/* Workflow step badge, Prequal badge and Simulated badge stacked vertically */}
                          <Stack gap={4} align="flex-end">
                            {conversation.currentWorkflow && conversation.workflowStep && (
                              <Tooltip label={`${conversation.currentWorkflow} - ${conversation.workflowStep}`}>
                                <Badge
                                  size="md"
                                  color={conversation.workflowStep === 'HUMAN_HANDOFF' ? 'green' : 'blue'}
                                  variant="dot"
                                >
                                  {conversation.workflowStep}
                                </Badge>
                              </Tooltip>
                            )}
                            {/* Prequal status badge */}
                            {getPrequalBadge(conversation)}
                          </Stack>
                          <Group gap="xs">
                            {getConversationStatusBadge(conversation)}
                            {getStatusBadge(conversation)}
                          </Group>
                        </Group>
                      </Group>

                      {/* Vehicle */}
                      {conversation.vehicleInterest && (
                        <VehicleInterestBadge
                          label={conversation.vehicleInterest}
                          details={conversation.vehicleInterestDetails}
                          variant="text"
                          size="xs"
                          c="dimmed"
                          truncate
                        />
                      )}

                      {/* Metadata */}
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs">
                          <Text size="xs" c="dimmed">
                            {conversation.leadSource || 'Direct'}
                          </Text>
                          <Text size="xs" c="dimmed">
                            •
                          </Text>
                          <Text size="xs" c="dimmed">
                            {formatDuration(conversation.startedAt)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            •
                          </Text>
                          <Text size="xs" c="dimmed">
                            {conversation.messageCount} msgs
                          </Text>
                        </Group>
                        <Group gap="xs">
                          {conversation.agentAssigned && (
                            <Badge size="md" variant="light" leftSection={<IconRobot size={14} />}>
                              Agent
                            </Badge>
                          )}
                        </Group>
                      </Group>
                    </Stack>
                  </Box>
                )
              })}

              {filteredConversations.length === 0 && !isLoading && (
                <Box p="xl">
                  <Text size="sm" c="dimmed" ta="center">
                    No conversations found
                  </Text>
                </Box>
              )}

              {/* Loading more indicator */}
              {isLoadingMore && (
                <Box p="md">
                  <Text size="sm" c="dimmed" ta="center">
                    Loading more conversations...
                  </Text>
                </Box>
              )}

              {/* End of list indicator */}
              {!hasMore && filteredConversations.length > 0 && (
                <Box p="md">
                  <Text size="sm" c="dimmed" ta="center">
                    No more conversations to load
                  </Text>
                </Box>
              )}
            </Stack>
          </ScrollArea>

          <LoadingOverlay visible={isLoading} />
        </Box>
      </Box>

      {/* Right Side - Conversation Detail + Timeline dock */}
      <Box
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          minWidth: 0,
        }}
      >
        <Box style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {selectedConversation ? (
            <ConversationDetail
              conversation={selectedConversation}
              onUpdate={silentRefreshActiveConversation}
              openSummary={openSummary}
              onCloseSummary={() => setOpenSummary(false)}
              isEvalCustomer={evalCustomerIds.has(selectedConversation.customerId)}
            />
          ) : (
            <Box style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Stack align="center" gap="md">
                <IconMessage size={64} stroke={1} opacity={0.3} />
                <Text size="lg" c="dimmed">
                  Select a conversation to view details
                </Text>
              </Stack>
            </Box>
          )}
        </Box>
        {selectedConversation ? (
          <CustomerExternalEventsDock externalEvents={selectedConversation.customerExternalEvents} />
        ) : null}
      </Box>

      {/* Evaluation Results Modal */}
      <EvaluationResultsModal
        opened={openEvaluation}
        onClose={() => {
          setOpenEvaluation(false)
          setEvaluationData(null)
        }}
        evaluationData={evaluationData}
        loading={loadingEvaluation}
      />
    </Box>
  )
}
